import { Exercise, SetItem } from '@/stores/store.types';
import chunk from 'lodash/chunk';
import flattenDeep from 'lodash/flattenDeep';
import moment from 'moment';
import { Dimensions, Platform } from 'react-native';
import store from '../stores/store';
import { logInfo } from './logHandling';

let { width, height } = Dimensions.get('window');

let guidelineBaseWidth = 350;
let guidelineBaseHeight = 680;

const starTime = 1 * 60 * 10; // 10 means 10 minutes for each exercise

let scale = (size = 1) => (width / guidelineBaseWidth) * size;
let verticalScale = (size = 1) => (height / guidelineBaseHeight) * size;
let moderateScale = (size = 1, factor = 0.5) =>
  size + (scale(size) - size) * factor;

let utils = {
  //  Get random elements from array
  getRandom: (arr: unknown[], n: number) => {
    let result = new Array(n),
      len = arr.length,
      taken = new Array(len);
    if (n > len) {
      n = len;
      //throw new RangeError("getRandom: more elements taken than available");
    }
    while (n--) {
      let x = Math.floor(Math.random() * len);
      result[n] = arr[x in taken ? taken[x] : x];
      taken[x] = --len in taken ? taken[len] : len;
    }
    return result;
  },

  //  Shuffle Array
  shuffle: (array: unknown[]) => {
    let currentIndex = array.length,
      temporaryValue,
      randomIndex;

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      // And swap it with the current element.
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }

    return array;
  },

  /**
   * @param itemSettings the currentSettings being sent from the setting component could be [wordsetExercisesTypes, sentencesetExercisesTypes, textsetExercisesTypes]
   * @param exerciseType type of the exercise to change could be [Sprechen, Lesen, Schreiben, Verstehen]
   * @param setType type of set could be [sentence, word, text]
   * @param setting will check what field of data to check [setting or value ]
   * @param value the sent value that will be compared with in the settings object when matched
   * @param settingValue this is used only for small cases to check some settings that don't follow the flow
   * @returns {boolean|*}
   */
  determineSettings: (
    itemSettings: {
      name: string;
      value: unknown;
      [x: string]: unknown;
    }[],
    exerciseType: string,
    setType: string,
    setting: string,
    value: unknown,
    settingValue = '',
  ) => {
    // itemSettings could be wordSettings, sentenceSettings, textSettings
    // Check the setting exerciseType and then check the value of it to assign true or false on select button
    if (setType === 'word' || setType === 'sentence') {
      let settings = itemSettings.filter((item) => {
        // Mainly values of settings are checked here
        if (
          settingValue === '' &&
          item.name.toLowerCase() === exerciseType.toLowerCase() &&
          item[setting] === value
        ) {
          return item;
          // to check the case where recording duration number
        } else if (
          settingValue === 'recordingDuration' &&
          item.name.toLowerCase() === exerciseType.toLowerCase()
        ) {
          return item;
          // to check values when setting value is set and show like a comment what is being checked
        } else if (
          settingValue &&
          item.name.toLowerCase() === exerciseType.toLowerCase() &&
          item.value === value
        ) {
          return item;
        }
      });

      if (settingValue === 'recordingDuration') {
        return settings?.[0]?.value;
      } else {
        return settings.length > 0;
      }
    } else if (setType === 'text') {
      // for text audio speed settings
      if (!exerciseType) {
        return itemSettings === value;
      } else {
        let settings = itemSettings.filter((settings) => {
          // for text settings is closed or open
          if (
            settingValue === '' &&
            settings.name.toLowerCase() === exerciseType.toLowerCase() &&
            settings[setting] === value
          ) {
            return settings;

            // for text settings is closed, questions gap
          } else if (
            settingValue &&
            settings.name.toLowerCase() === exerciseType.toLowerCase() &&
            settings.value === value
          ) {
            return settings;
          }
        });

        return settings.length > 0;
      }
    }
  },
  getAllSetsImages: (chosenSet: any, objectTitle: string) => {
    // Collect all images from the current sets
    let allSetImages = chosenSet.setItems.map((setItem: any) => {
      if (setItem.word && setItem.word.print !== objectTitle) {
        return [...setItem.word.pictures];
      } else if (setItem.sentence && setItem.sentence.print !== objectTitle) {
        return [...setItem.sentence.pictures];
      }
    });

    allSetImages = allSetImages.filter((item: any) => item !== undefined);

    // Flatten the collected images arrays to one array
    allSetImages = [].concat.apply([], allSetImages);

    // Get random 3 images from the allSetImages
    allSetImages = utils.getRandom(allSetImages, 3);

    return allSetImages;
  },
  convertMinutesToSeconds: (minutes: number) => {
    return minutes * 60;
  },

  getPercentageOf: (currentValue: number, maxNumber: number) => {
    const hundred = 100;
    return ((currentValue / maxNumber) * parseFloat(hundred.toFixed(3))) / 100;
  },
  getCurrentSetitemType: (props: {
    isWordset: boolean;
    isSentenceset: boolean;
  }) => {
    let setitemType = '';
    if (props.isWordset) {
      setitemType = 'wordsetitem';
    }
    if (props.isSentenceset) {
      setitemType = 'sentencesetitem';
    }
    return setitemType;
  },
  pushResultsAndReset: () => {
    // this process to remove _id from the result before pushing
    let currentTrackResult = store.getState().trackResult;
    delete currentTrackResult.exerciseType._id;

    if (currentTrackResult.uuid !== '' && currentTrackResult.realUuid !== '') {
      store.getActions().setResultTrack(currentTrackResult);
      store
        .getActions()
        .setResultsWithKey({ data: store.getState().trackResult });
      store.getActions().setResultTrack('reset');
    }
  },
  startPracticeSessionTime: () => {
    if (
      !store.getState().trackPracticeSession.startDate &&
      !store.getState().trackPracticeSession.uuid
    ) {
      let timestamp = Date.now();
      store
        .getActions()
        .setPracticeSessionTrack({ startDate: timestamp, uuid: timestamp });
      store.getActions().setStartPracticeSessionTime(new Date());
    }
  },
  endPracticeSessionTime: () => {
    // Check if start times are not undefined to end the session and add it to the track session object
    if (
      !store.getState().trackPracticeSession.startDate &&
      !store.getState().trackPracticeSession.uuid
    ) {
      let timestamp = Date.now();
      store.getActions().setPracticeSessionTrack({ endDate: timestamp });
      store.getActions().setEndPracticeSessionTime(new Date());
    }
  },
  pushPracticeSessionTrackAndReset: () => {
    if (store.getState().trackPracticeSession.startDate) {
      let endDate = Date.now();

      store.getActions().setPracticeSessionTrack({ endDate: endDate });

      store.getActions().setPracticeSessionsWithKey({
        data: store.getState().trackPracticeSession,
      });
      store.getActions().setPracticeSessionTrack('reset');
    }
  },
  getTheLatestPracticeSessionTime: (practiceDurations: unknown[]) => {
    let seconds = 0;
    let todaysPracticeDuration = practiceDurations.find((duration) => {
      const dayWithZero =
        parseInt(duration._id.day) >= 10
          ? duration._id.day
          : `0${duration._id.day}`;
      const monthWithZero =
        parseInt(duration._id.month) >= 10
          ? duration._id.month
          : `0${duration._id.month}`;
      const date = `${dayWithZero}-${monthWithZero}-${duration._id.year}`;
      const todayDate = moment().format('DD-MM-yyyy');

      if (todayDate === date) {
        return duration.duration;
      }
    });

    if (todaysPracticeDuration) {
      seconds = Math.floor(todaysPracticeDuration.duration / 1000);
    }

    store.getActions().setTodayExerciseTimeInSeconds(seconds);
    const remainingTime = (seconds / 60) % 10;

    store
      .getActions()
      .setRemainingTodayExerciseTimeInMinutes(Math.floor(remainingTime));
  },
  determinePerformance: (mistakes: number) => {
    if (mistakes === 0) {
      return 'Richtig';
    }
    if (mistakes === 1) {
      return 'Fast';
    }
    if (mistakes > 1) {
      return 'Falsch';
    }
  },
  escapeCode: (setCode: string) => {
    return setCode?.split('#').join('%23');
  },
  convertSecondsToMinutes: (seconds: number) => {
    return seconds / 60;
  },
  convertMillisToMinutes: (millis: number) => {
    return millis / 60000;
  },
  userLeftExerciseBefore10MinutesFinished: () => {
    if (
      store.getState().currentPracticeSessionDuration <
      utils.convertMinutesToSeconds(10)
    ) {
      store.getActions().setUserLeftExerciseBeforeFinishing10Minutes();
      store
        .getActions()
        .setLastTimeDurationUserExercisedBeforeLeavingTheExerciseWithoutFinishing10Minutes(
          store.getState().currentPracticeSessionDuration,
        );
    }
  },
  getOnlyDividableBy10Number: (number: number) => {
    return Math.floor(number / 10) * 10;
  },

  getRemainingMinutesFromSeconds: (totalSeconds: number) => {
    return (totalSeconds % starTime) / starTime;
  },

  getLastDurationUserExercised: () => {
    let startDate = store.getState().startPracticeSessionTime;
    let endDate = new Date();

    let timeDifferenceInSeconds =
      (endDate.getTime() - startDate.getTime()) / 1000; // In seconds

    return Math.floor(timeDifferenceInSeconds);
  },
  setLocalTotalExerciseTime: () => {
    store
      .getActions()
      .setTodayExerciseTimeInSeconds(store.getState().exercisedDuration);
  },
  getTodayDateStars: () => {
    return (
      store.getState().starContainer?.container?.lastProgress || {
        date: '',
        stars: 0,
      }
    );
  },
  secondsToStar: () => {
    return store.getState().exercisedDuration / starTime;
  },
  checkToShowResultsPopUp: () => {
    let stars = Math.floor(utils.secondsToStar());
    let todayDate = utils.getTodayDateStars().date;
    let lastStars = utils.getTodayDateStars().stars;

    if (todayDate === moment().format('YYYY-MM-DD') && lastStars < stars) {
      // Update lastProgress in storage to save when was upadted
      store.getActions().setStarContainer({
        container: {
          lastProgress: {
            date: moment().format('YYYY-MM-DD'),
            stars: stars,
          },
        },
      });
      store.getActions().setStarsPopUp();
      store.getActions().setCurrentPracticeSessionDuration(1);
      return true;
    } else {
      store.getActions().setStarContainer({
        container: {
          lastProgress: {
            date: moment().format('YYYY-MM-DD'),
            stars: stars,
          },
        },
      });
    }
  },
  chunkExerciseItems: (exercises: Exercise[], setItems: SetItem[]) => {
    if (exercises.length > 0) {
      let itemsChunks = chunk(setItems, 4);
      const updatedExercisesWithTheirItems = itemsChunks.map((items) => {
        return exercises.map((exercise) => {
          return {
            ...exercise,
            taskItems: utils.shuffle(items),
          };
        });
      });

      return flattenDeep(updatedExercisesWithTheirItems);
    } else {
      return exercises;
    }
  },
  scale,
  verticalScale,
  moderateScale,
  starTime,
};

export const hasOwnProperty = (entry: unknown, key: string) => {
  return Object.prototype.hasOwnProperty.call(entry, key);
};

const initConsoleTracker = () => {
  // Hide all console logs in production
  let DEBUG = process.env.NODE_ENV !== 'production';
  if (!DEBUG) {
    if (!window.console) {
      window.console = {} as any;
    }
    let methods = ['log', 'debug', 'warn', 'info'];
    for (let i = 0; i < methods.length; i++) {
      // eslint-disable-next-line no-console
      console[methods[i] as keyof typeof console] = function () {} as any;
    }
  }
};

export const isCapital = (letter: string) => {
  return letter === letter.toUpperCase() && letter !== letter.toLowerCase();
};

const initWebHttpTracker = () => {
  // Force all requests to https on browser
  if (
    process.env.NODE_ENV === 'production' &&
    window.location.protocol !== 'https:' &&
    !(
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    )
  ) {
    window.location.replace(
      `https:${window.location.href.substring(window.location.protocol.length)}`,
    );
  }
};

const initWebPerformanceTracker = () => {
  if (window.performance) {
    if (window.performance.navigation.type == 1) {
      logInfo('This page is reloaded');
    } else {
      logInfo('This page is not reloaded');
    }
  }
};

export const initTrackers = (platform: Platform['OS']) => {
  initConsoleTracker();
  if (platform === 'web') {
    initWebHttpTracker();
    initWebPerformanceTracker();
  }
};

export default utils;
